import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";

const C = {
  navy: colors.darkBlue,
  red: colors.red,
  green: colors.green,
  slate: colors.slate,
  border: colors.border,
  bg: colors.lightGrey,
  white: colors.white,
  text: colors.text,
};

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtType(type) {
  return (type || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function PhotoThumb({ url, onClick }) {
  return (
    <img
      src={url}
      alt="defect"
      onClick={() => onClick(url)}
      style={{
        width: 80,
        height: 60,
        objectFit: "cover",
        borderRadius: 6,
        border: `1px solid ${C.border}`,
        cursor: "pointer",
        flexShrink: 0,
      }}
    />
  );
}

function Lightbox({ url, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}
    >
      <img
        src={url}
        alt="defect full"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, objectFit: "contain" }}
      />
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 20,
          right: 24,
          background: "none",
          border: "none",
          color: "#fff",
          fontSize: 28,
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}

function DefectCard({ defect, onPhotoClick, isRectified }) {
  const photos = [defect.photo_1_url, defect.photo_2_url, defect.photo_3_url].filter(Boolean);
  const assetLine = [defect.asset_type_name, defect.floor_level, defect.location].filter(Boolean).join(" · ");
  const metaLine = [defect.category_name, fmtType(defect.inspection_type), fmtDate(defect.inspection_date)].filter(Boolean).join(" · ");

  return (
    <div
      style={{
        borderLeft: `4px solid ${isRectified ? C.green : C.red}`,
        backgroundColor: isRectified ? "#f0fdf4" : "#fff8f8",
        borderRadius: "0 8px 8px 0",
        padding: "12px 16px",
        marginBottom: 8,
      }}
    >
      <div style={{ fontWeight: 600, color: C.navy, fontSize: 14, marginBottom: 2 }}>{assetLine || "Asset"}</div>
      <div style={{ fontSize: 12, color: C.slate, marginBottom: 6 }}>{metaLine}</div>
      {defect.notes && (
        <div style={{ fontSize: 13, color: C.text, marginBottom: photos.length ? 10 : 0 }}>{defect.notes}</div>
      )}
      {photos.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {photos.map((url, i) => (
            <PhotoThumb key={i} url={url} onClick={onPhotoClick} />
          ))}
        </div>
      )}
      {isRectified && defect.rectification && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid #bbf7d0` }}>
          <div style={{ fontSize: 12, color: C.green, fontWeight: 600, marginBottom: 4 }}>
            Rectified{defect.rectification.tech ? ` by ${defect.rectification.tech}` : ""}
            {defect.rectification.attested_at ? ` · ${fmtDate(defect.rectification.attested_at)}` : ""}
          </div>
          {defect.rectification.actions_taken && (
            <div style={{ fontSize: 13, color: C.text, marginBottom: defect.rectification.photo_url ? 8 : 0 }}>
              {defect.rectification.actions_taken}
            </div>
          )}
          {defect.rectification.photo_url && (
            <PhotoThumb url={defect.rectification.photo_url} onClick={onPhotoClick} />
          )}
        </div>
      )}
    </div>
  );
}

function SiteCard({ group, expanded, onToggle, onPhotoClick, isRectified }) {
  const count = group.defects.length;
  return (
    <div style={{ ...styles.card, marginBottom: 12 }}>
      <div
        onClick={onToggle}
        style={{
          padding: "14px 20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: expanded ? "#f8fafc" : C.white,
          borderBottom: expanded ? `1px solid ${C.border}` : "none",
          borderRadius: expanded ? "12px 12px 0 0" : 12,
          transition: "background 0.15s",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>{group.site_name}</div>
          {group.site_address && (
            <div style={{ fontSize: 12, color: C.slate, marginTop: 2 }}>{group.site_address}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              backgroundColor: isRectified ? "#dcfce7" : "#fee2e2",
              color: isRectified ? C.green : C.red,
              fontWeight: 700,
              fontSize: 13,
              padding: "3px 12px",
              borderRadius: 20,
            }}
          >
            {count} {count === 1 ? "defect" : "defects"}
          </span>
          <span style={{ color: C.slate, fontSize: 18, lineHeight: 1 }}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>
      {expanded && (
        <div style={{ padding: "12px 16px" }}>
          {group.defects.map((d) => (
            <DefectCard key={d.id} defect={d} onPhotoClick={onPhotoClick} isRectified={isRectified} />
          ))}
        </div>
      )}
    </div>
  );
}

export function DefectsRegisterPage() {
  const [tab, setTab] = useState("open");
  const [loading, setLoading] = useState(true);
  const [inspections, setInspections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [expandedSites, setExpandedSites] = useState(new Set());
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/inspections/`),
      axios.get(`${API}/rectification-requests/`),
    ])
      .then(([inspRes, reqRes]) => {
        setInspections(inspRes.data || []);
        setRequests(reqRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const { openBySite, rectifiedBySite } = useMemo(() => {
    // Build attested map: inspection_item_id → rectification details
    const attestedMap = {};
    requests.forEach((req) => {
      (req.items || []).forEach((item) => {
        if (item.attested) {
          const inspItemId = item.quote_item_detail?.inspection_item;
          if (inspItemId) {
            attestedMap[inspItemId] = {
              actions_taken: item.actions_taken,
              photo_url: item.photo_url,
              attested_at: item.attested_at,
              tech: req.assigned_to_name,
            };
          }
        }
      });
    });

    // Collect all fail items
    const openItems = [];
    const rectifiedItems = [];
    inspections.forEach((insp) => {
      (insp.items || [])
        .filter((item) => item.result === "fail" && !item.swapped)
        .forEach((item) => {
          const rec = attestedMap[item.id] || null;
          const enriched = {
            id: item.id,
            site_id: insp.site,
            site_name: insp.site_name,
            site_address: insp.site_address,
            inspection_date: insp.date,
            inspection_type: insp.inspection_type,
            asset_type_name: item.asset_type_name,
            category_name: item.category_name,
            floor_level: item.floor_level,
            location: item.location,
            notes: item.notes,
            photo_1_url: item.photo_1_url,
            photo_2_url: item.photo_2_url,
            photo_3_url: item.photo_3_url,
            rectification: rec,
          };
          if (rec) rectifiedItems.push(enriched);
          else openItems.push(enriched);
        });
    });

    function groupBySite(items) {
      const map = {};
      items.forEach((item) => {
        const key = item.site_id;
        if (!map[key]) {
          map[key] = { site_id: key, site_name: item.site_name, site_address: item.site_address, defects: [] };
        }
        map[key].defects.push(item);
      });
      return Object.values(map).sort((a, b) => (a.site_name || "").localeCompare(b.site_name || ""));
    }

    const openBySite = groupBySite(openItems).map((g) => ({
      ...g,
      defects: g.defects.sort((a, b) => new Date(a.inspection_date) - new Date(b.inspection_date)),
    }));

    const rectifiedBySite = groupBySite(rectifiedItems).map((g) => ({
      ...g,
      defects: g.defects.sort(
        (a, b) => new Date(b.rectification?.attested_at) - new Date(a.rectification?.attested_at)
      ),
    }));

    return { openBySite, rectifiedBySite };
  }, [inspections, requests]);

  const currentGroups = tab === "open" ? openBySite : rectifiedBySite;
  const isRectified = tab === "rectified";
  const openTotal = openBySite.reduce((s, g) => s + g.defects.length, 0);
  const rectTotal = rectifiedBySite.reduce((s, g) => s + g.defects.length, 0);

  function toggleSite(siteId) {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  // Reset expanded sites when switching tabs
  function handleTabChange(t) {
    setTab(t);
    setExpandedSites(new Set());
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Top bar */}
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Defect Register</div>
      </div>

      {/* Tab bar */}
      <div style={{ backgroundColor: C.white, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", paddingLeft: 24 }}>
          {[
            ["open", "Open Defects", openTotal],
            ["rectified", "Rectified", rectTotal],
          ].map(([id, label, count]) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              style={{
                background: "none",
                border: "none",
                borderBottom: tab === id ? `3px solid ${C.red}` : "3px solid transparent",
                padding: "14px 20px",
                fontSize: 14,
                fontWeight: tab === id ? 700 : 400,
                color: tab === id ? C.navy : C.slate,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {label}
              {count > 0 && (
                <span
                  style={{
                    backgroundColor: id === "open" ? "#fee2e2" : "#dcfce7",
                    color: id === "open" ? C.red : C.green,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 20,
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ ...styles.content, overflowY: "auto" }}>
        {loading ? (
          <div style={styles.loading}>Loading defects...</div>
        ) : currentGroups.length === 0 ? (
          <div
            style={{
              padding: "60px 40px",
              textAlign: "center",
              color: C.slate,
              fontSize: 15,
            }}
          >
            {tab === "open"
              ? "No open defects — all reported defects have been rectified."
              : "No rectified defects yet."}
          </div>
        ) : (
          currentGroups.map((group) => (
            <SiteCard
              key={group.site_id}
              group={group}
              expanded={expandedSites.has(group.site_id)}
              onToggle={() => toggleSite(group.site_id)}
              onPhotoClick={(url) => setLightboxUrl(url)}
              isRectified={isRectified}
            />
          ))
        )}
      </div>

      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}
